import { useMemo } from 'react';
import {
  Checkbox,
  Dropdown,
  Field,
  Option,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useFetch } from '@moko/ui';
import { useTranslation } from 'react-i18next';
import {
  getCertificationTypes,
  getWorkTypeCertifications,
  getWorkTypes,
  type ICertificationType,
  type IWorkType,
  type IWorkTypeCertification,
} from '../../services/settingsService';
import type { ITenantWorkConfig } from '../../services/tenantService';

export interface TenantWorkFieldsProps {
  value: ITenantWorkConfig;
  onChange: (value: ITenantWorkConfig) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    width: '100%',
    maxWidth: '640px',
    boxSizing: 'border-box',
  },
  field: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  nested: {
    paddingLeft: tokens.spacingHorizontalXXL,
  },
});

const bySortOrder = <T extends { sortOrder: number; id?: number }>(
  left: T,
  right: T
) => left.sortOrder - right.sortOrder || (left.id ?? 0) - (right.id ?? 0);

const certificationLabel = (item: { code: string; name: string }) =>
  item.name && item.name !== item.code
    ? `${item.code} — ${item.name}`
    : item.name || item.code;

const selectedIds = (options: string[]): number[] => {
  const ids = options
    .map((option) => Number(option))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
};

const TenantWorkFields = ({
  value,
  onChange,
  disabled = false,
}: TenantWorkFieldsProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const workTypesFetch = useFetch<IWorkType[]>({
    queryKey: ['workTypes'],
    queryFn: getWorkTypes,
  });
  const certificationsFetch = useFetch<ICertificationType[]>({
    queryKey: ['certificationTypes'],
    queryFn: getCertificationTypes,
  });
  const linksFetch = useFetch<IWorkTypeCertification[]>({
    queryKey: ['workTypeCertifications'],
    queryFn: getWorkTypeCertifications,
  });

  const workTypes = useMemo(
    () =>
      [...(workTypesFetch.data ?? [])]
        .filter((item) => item.id != null)
        .sort(bySortOrder),
    [workTypesFetch.data]
  );
  const certifications = useMemo(
    () =>
      [...(certificationsFetch.data ?? [])]
        .filter((item) => item.id != null)
        .sort(bySortOrder),
    [certificationsFetch.data]
  );
  const suggestions = useMemo(() => {
    const order = new Map(
      certifications.map((item) => [item.id as number, item.sortOrder])
    );
    const grouped = new Map<number, number[]>();
    for (const link of linksFetch.data ?? []) {
      const current = grouped.get(link.workTypeId) ?? [];
      if (!current.includes(link.certificationTypeId)) {
        current.push(link.certificationTypeId);
      }
      grouped.set(link.workTypeId, current);
    }
    for (const ids of grouped.values()) {
      ids.sort(
        (left, right) => (order.get(left) ?? 0) - (order.get(right) ?? 0)
      );
    }
    return grouped;
  }, [certifications, linksFetch.data]);

  const enabled = new Map(
    value.workTypes.map((item) => [item.workTypeId, item.certificationTypeIds])
  );
  const certificationById = new Map(
    certifications.map((item) => [item.id as number, item])
  );

  const certificationOptions = (ids: number[]) => {
    const known = new Set(certifications.map((item) => item.id));
    return [
      ...certifications,
      ...ids
        .filter((id) => !known.has(id))
        .map((id) => ({
          id,
          code: String(id),
          name: String(id),
          sortOrder: Number.MAX_SAFE_INTEGER,
        })),
    ];
  };

  const labelsFor = (ids: number[]) =>
    ids
      .map((id) => {
        const item = certificationById.get(id);
        return item ? certificationLabel(item) : String(id);
      })
      .join(', ');

  const setTenantCertifications = (ids: number[]) => {
    onChange({ ...value, certificationTypeIds: ids });
  };

  const setWorkTypeCertifications = (
    workTypeId: number,
    certificationTypeIds: number[]
  ) => {
    onChange({
      ...value,
      workTypes: value.workTypes.map((item) =>
        item.workTypeId === workTypeId
          ? { ...item, certificationTypeIds }
          : item
      ),
    });
  };

  const toggleWorkType = (workTypeId: number, checked: boolean) => {
    if (checked) {
      if (enabled.has(workTypeId)) return;
      onChange({
        ...value,
        workTypes: [
          ...value.workTypes,
          {
            workTypeId,
            certificationTypeIds: [...(suggestions.get(workTypeId) ?? [])],
          },
        ],
      });
      return;
    }
    onChange({
      ...value,
      workTypes: value.workTypes.filter(
        (item) => item.workTypeId !== workTypeId
      ),
    });
  };

  const catalogueError = workTypesFetch.error
    ? t('workTypesLoadFailed', 'Could not load work types.')
    : certificationsFetch.error
    ? t('certificationTypesLoadFailed', 'Could not load certifications.')
    : linksFetch.error
    ? t(
        'workTypeCertificationsLoadFailed',
        'Could not load suggested certifications.'
      )
    : undefined;
  const loading =
    workTypesFetch.loading || certificationsFetch.loading || linksFetch.loading;
  const listedWorkTypes = [
    ...workTypes,
    ...value.workTypes
      .filter(
        (item) => !workTypes.some((workType) => workType.id === item.workTypeId)
      )
      .map((item) => ({
        id: item.workTypeId,
        code: String(item.workTypeId),
        name: `Work type ${item.workTypeId}`,
        sortOrder: Number.MAX_SAFE_INTEGER,
      })),
  ];

  return (
    <div className={styles.root}>
      {catalogueError ? <Text>{catalogueError}</Text> : null}
      {loading ? (
        <Text>{t('loading', 'Loading...')}</Text>
      ) : (
        <>
          <div className={styles.group}>
            <Text weight="semibold">
              {t('tenantCertifications', 'Certifications')}
            </Text>
            <Text>
              {t(
                'tenantCertificationsHint',
                'Required for every work belonging to this tenant.'
              )}
            </Text>
            <Dropdown
              className={styles.field}
              multiselect
              disabled={disabled}
              aria-label={t('tenantCertifications', 'Certifications')}
              selectedOptions={value.certificationTypeIds.map(String)}
              value={labelsFor(value.certificationTypeIds)}
              placeholder={t('selectCertifications', 'Select certifications')}
              onOptionSelect={(_, data) =>
                setTenantCertifications(selectedIds(data.selectedOptions))
              }
            >
              {certificationOptions(value.certificationTypeIds).map((item) => (
                <Option key={item.id} value={String(item.id)}>
                  {certificationLabel(item)}
                </Option>
              ))}
            </Dropdown>
          </div>
          <div className={styles.group}>
            <Text weight="semibold">{t('workTypes', 'Work types')}</Text>
            <Text>
              {t(
                'workTypesHint',
                'Enabled work types can require additional certifications.'
              )}
            </Text>
            {listedWorkTypes.length === 0 && !workTypesFetch.error ? (
              <Text>{t('noWorkTypes', 'No work types.')}</Text>
            ) : (
              listedWorkTypes.map((workType) => {
                const workTypeId = workType.id as number;
                const checked = enabled.has(workTypeId);
                const certificationTypeIds = enabled.get(workTypeId) ?? [];
                return (
                  <div key={workTypeId}>
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      label={workType.name || workType.code}
                      onChange={(_, data) =>
                        toggleWorkType(workTypeId, data.checked === true)
                      }
                    />
                    {checked ? (
                      <div className={styles.nested}>
                        <Field
                          className={styles.field}
                          label={t(
                            'workTypeCertifications',
                            'Additional certifications'
                          )}
                        >
                          <Dropdown
                            className={styles.field}
                            multiselect
                            disabled={disabled}
                            selectedOptions={certificationTypeIds.map(String)}
                            value={labelsFor(certificationTypeIds)}
                            placeholder={t(
                              'selectCertifications',
                              'Select certifications'
                            )}
                            onOptionSelect={(_, data) =>
                              setWorkTypeCertifications(
                                workTypeId,
                                selectedIds(data.selectedOptions)
                              )
                            }
                          >
                            {certificationOptions(certificationTypeIds).map(
                              (item) => (
                                <Option key={item.id} value={String(item.id)}>
                                  {certificationLabel(item)}
                                </Option>
                              )
                            )}
                          </Dropdown>
                        </Field>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TenantWorkFields;
